/**
 * ProjectImageUploader
 *
 * Drag-and-drop image upload with preview grid, cover selection,
 * reorder capability, and delete actions.
 */
import { useState, useRef, useCallback } from "react";
import {
  Upload,
  X,
  Star,
  Loader2,
  GripVertical,
  ImagePlus,
} from "lucide-react";
import type { ProjectImage } from "../lib/projectImages";

interface ProjectImageUploaderProps {
  images: ProjectImage[];
  uploading: boolean;
  onUpload: (files: File[]) => Promise<void>;
  onDelete: (imageId: string) => Promise<void>;
  onMakeCover: (imageId: string) => Promise<void>;
  onReorder?: (imageIds: string[]) => Promise<void>;
  maxFiles?: number;
  /** Whether to show in compact mode (e.g. inside modals) */
  compact?: boolean;
}

const ACCEPTED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/avif",
];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export function ProjectImageUploader({
  images,
  uploading,
  onUpload,
  onDelete,
  onMakeCover,
  onReorder,
  compact = false,
}: ProjectImageUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [pendingPreviews, setPendingPreviews] = useState<
    { file: File; preview: string }[]
  >([]);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Drag-and-drop reorder state
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [dragSide, setDragSide] = useState<"left" | "right">("left");

  const validateFiles = useCallback((files: File[]): File[] => {
    return files.filter((file) => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        console.warn("Skipped a file: unsupported type");
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        console.warn(`Skipped a file: exceeds 50MB limit`);
        return false;
      }
      return true;
    });
  }, []);

  const handleFiles = useCallback(
    async (fileList: FileList | File[]) => {
      const files = validateFiles(Array.from(fileList));
      if (files.length === 0) return;

      // Create local previews
      const previews = files.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));
      setPendingPreviews(previews);

      try {
        await onUpload(files);
      } finally {
        // Revoke object URLs
        previews.forEach((p) => URL.revokeObjectURL(p.preview));
        setPendingPreviews([]);
      }
    },
    [onUpload, validateFiles]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFiles(e.target.files);
        e.target.value = "";
      }
    },
    [handleFiles]
  );

  const handleDelete = useCallback(
    async (imageId: string) => {
      await onDelete(imageId);
      setDeleteConfirm(null);
    },
    [onDelete]
  );

  // ── Drag-and-drop reorder handlers ──
  const handleReorderDragStart = useCallback(
    (e: React.DragEvent, imageId: string) => {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", imageId);
      setDraggedId(imageId);
    },
    []
  );

  const handleReorderDragOver = useCallback(
    (e: React.DragEvent, imageId: string) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (imageId === draggedId) return;

      // Determine which half the cursor is on
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const midX = rect.left + rect.width / 2;
      setDragSide(e.clientX < midX ? "left" : "right");
      setDragOverId(imageId);
    },
    [draggedId]
  );

  const handleReorderDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      if (!draggedId || !dragOverId || draggedId === dragOverId || !onReorder)
        return;

      const oldOrder = images.map((img) => img.id);
      const fromIdx = oldOrder.indexOf(draggedId);
      const toIdx = oldOrder.indexOf(dragOverId);
      if (fromIdx === -1 || toIdx === -1) return;

      // Remove dragged item
      const newOrder = oldOrder.filter((id) => id !== draggedId);
      // Insert at the correct position based on drop side
      let insertIdx = newOrder.indexOf(dragOverId);
      if (dragSide === "right") insertIdx += 1;
      newOrder.splice(insertIdx, 0, draggedId);

      setDraggedId(null);
      setDragOverId(null);
      await onReorder(newOrder);
    },
    [draggedId, dragOverId, dragSide, images, onReorder]
  );

  const handleReorderDragEnd = useCallback(() => {
    setDraggedId(null);
    setDragOverId(null);
  }, []);

  const dropZoneHeight = compact ? 100 : 140;
  const gridSize = compact ? 80 : 110;

  return (
    <div>
      {/* Image grid: existing + pending uploads */}
      {(images.length > 0 || pendingPreviews.length > 0) && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(auto-fill, minmax(${gridSize}px, 1fr))`,
            gap: 8,
            marginBottom: 12,
          }}
        >
          {images.map((image, idx) => {
            const isBeingDragged = draggedId === image.id;
            const isDropTarget = dragOverId === image.id && draggedId !== image.id;

            return (
              <div
                key={image.id}
                draggable={!!onReorder && images.length > 1}
                onDragStart={(e) => handleReorderDragStart(e, image.id)}
                onDragOver={(e) => handleReorderDragOver(e, image.id)}
                onDrop={handleReorderDrop}
                onDragEnd={handleReorderDragEnd}
                style={{
                  position: "relative",
                  aspectRatio: "1",
                  borderRadius: 8,
                  overflow: "hidden",
                  border: image.is_cover
                    ? "2px solid oklch(0.75 0.15 85)"
                    : "1px solid var(--border-color)",
                  transition: "border-color 0.2s ease, opacity 0.2s ease, transform 0.2s ease",
                  opacity: isBeingDragged ? 0.35 : 1,
                  transform: isBeingDragged ? "scale(0.95)" : "scale(1)",
                  // Drop indicator: blue bar on left or right edge
                  boxShadow: isDropTarget
                    ? dragSide === "left"
                      ? "inset 3px 0 0 oklch(0.6 0.2 250)"
                      : "inset -3px 0 0 oklch(0.6 0.2 250)"
                    : undefined,
                }}
              >
                <img
                  src={image.url}
                  alt={image.original_filename || "Project image"}
                  draggable={false}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />

                {/* Hover overlay with actions */}
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "oklch(0.1 0 0 / 0)",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    padding: 4,
                    transition: "background 0.2s ease",
                    cursor: "default",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background =
                      "oklch(0.1 0 0 / 0.5)";
                    const buttons =
                      e.currentTarget.querySelectorAll<HTMLElement>(
                        "[data-action-btn]"
                      );
                    buttons.forEach((b) => (b.style.opacity = "1"));
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background =
                      "oklch(0.1 0 0 / 0)";
                    const buttons =
                      e.currentTarget.querySelectorAll<HTMLElement>(
                        "[data-action-btn]"
                      );
                    buttons.forEach((b) => (b.style.opacity = "0"));
                  }}
                >
                  {/* Cover star */}
                  <button
                    data-action-btn
                    title={
                      image.is_cover
                        ? "Cover image"
                        : "Set as cover"
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!image.is_cover) onMakeCover(image.id);
                    }}
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 4,
                      border: "none",
                      background: image.is_cover
                        ? "oklch(0.75 0.15 85)"
                        : "oklch(0.2 0 0 / 0.6)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: image.is_cover
                        ? "default"
                        : "pointer",
                      opacity: image.is_cover ? 1 : 0,
                      transition: "opacity 0.15s ease",
                      padding: 0,
                    }}
                  >
                    <Star
                      size={12}
                      fill={image.is_cover ? "oklch(0.98 0 0)" : "none"}
                      color="oklch(0.95 0 0)"
                    />
                  </button>

                  {/* Delete */}
                  <button
                    data-action-btn
                    title="Remove image"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirm(image.id);
                    }}
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 4,
                      border: "none",
                      background: "oklch(0.45 0.15 25 / 0.85)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      opacity: 0,
                      transition: "opacity 0.15s ease",
                      padding: 0,
                    }}
                  >
                    <X size={12} color="oklch(0.95 0 0)" />
                  </button>
                </div>

                {/* Cover badge */}
                {image.is_cover && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: 4,
                      left: 4,
                      background: "oklch(0.75 0.15 85)",
                      borderRadius: 4,
                      padding: "1px 6px",
                      fontSize: "0.6rem",
                      fontWeight: 700,
                      color: "oklch(0.15 0 0)",
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                    }}
                  >
                    Cover
                  </div>
                )}

                {/* Order badge + Drag handle */}
                <div
                  style={{
                    position: "absolute",
                    bottom: 4,
                    right: 4,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    opacity: 0.6,
                    cursor: onReorder && images.length > 1 ? "grab" : "default",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.55rem",
                      fontWeight: 700,
                      color: "oklch(0.9 0 0)",
                      background: "oklch(0.15 0 0 / 0.5)",
                      borderRadius: 3,
                      padding: "0px 4px",
                      lineHeight: "14px",
                    }}
                  >
                    {idx + 1}
                  </span>
                  {onReorder && images.length > 1 && (
                    <GripVertical size={12} color="oklch(0.9 0 0)" />
                  )}
                </div>
              </div>
            );
          })}

          {/* Pending upload previews */}
          {pendingPreviews.map((p, idx) => (
            <div
              key={`pending-${idx}`}
              style={{
                position: "relative",
                aspectRatio: "1",
                borderRadius: 8,
                overflow: "hidden",
                border: "1px dashed var(--border-color)",
                opacity: 0.6,
              }}
            >
              <img
                src={p.preview}
                alt={`Uploading ${p.file.name}`}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                  filter: "brightness(0.7)",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Loader2
                  size={20}
                  color="oklch(0.95 0 0)"
                  className="animate-spin"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        style={{
          height: dropZoneHeight,
          borderRadius: 10,
          border: `2px dashed ${
            isDragging
              ? "oklch(0.65 0.15 250)"
              : "var(--border-color)"
          }`,
          background: isDragging
            ? "oklch(0.65 0.15 250 / 0.06)"
            : "var(--bg-tertiary)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          cursor: "pointer",
          transition:
            "border-color 0.2s ease, background 0.2s ease, transform 0.2s ease",
          transform: isDragging ? "scale(1.01)" : "scale(1)",
        }}
        onMouseEnter={(e) => {
          if (!isDragging) {
            (e.currentTarget as HTMLElement).style.borderColor =
              "oklch(0.55 0.08 250)";
          }
        }}
        onMouseLeave={(e) => {
          if (!isDragging) {
            (e.currentTarget as HTMLElement).style.borderColor =
              "var(--border-color)";
          }
        }}
      >
        {uploading ? (
          <>
            <Loader2
              size={24}
              className="animate-spin"
              style={{ color: "oklch(0.55 0.08 250)" }}
            />
            <span
              style={{
                fontSize: "0.8rem",
                color: "var(--text-secondary)",
                fontWeight: 500,
              }}
            >
              Uploading...
            </span>
          </>
        ) : (
          <>
            {isDragging ? (
              <Upload
                size={24}
                style={{ color: "oklch(0.55 0.12 250)" }}
              />
            ) : (
              <ImagePlus
                size={24}
                style={{ color: "var(--text-muted)" }}
              />
            )}
            <div style={{ textAlign: "center" }}>
              <span
                style={{
                  fontSize: "0.8rem",
                  color: isDragging
                    ? "oklch(0.55 0.12 250)"
                    : "var(--text-secondary)",
                  fontWeight: 500,
                }}
              >
                {isDragging
                  ? "Drop images here"
                  : "Drop images or click to browse"}
              </span>
              {!compact && (
                <p
                  style={{
                    fontSize: "0.7rem",
                    color: "var(--text-muted)",
                    marginTop: 4,
                  }}
                >
                  JPEG, PNG, WebP, AVIF • Max 50MB each
                </p>
              )}
            </div>
          </>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(",")}
        multiple
        onChange={handleInputChange}
        style={{ display: "none" }}
      />

      {/* Delete confirmation inline */}
      {deleteConfirm && (
        <div
          style={{
            marginTop: 8,
            padding: "8px 12px",
            background: "oklch(0.45 0.12 25 / 0.1)",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <span
            style={{
              fontSize: "0.8rem",
              color: "var(--text-secondary)",
            }}
          >
            Remove this image?
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setDeleteConfirm(null)}
              style={{ padding: "4px 10px", fontSize: "0.75rem" }}
            >
              Cancel
            </button>
            <button
              className="btn btn-sm"
              onClick={() => handleDelete(deleteConfirm)}
              style={{
                padding: "4px 10px",
                fontSize: "0.75rem",
                background: "oklch(0.50 0.18 25)",
                color: "oklch(0.98 0 0)",
                border: "none",
              }}
            >
              Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
